// ---------------------------------------------------------------------------
// Quem rola no app de iOS: o scroller do frame, não o documento (BUG-300).
//
// O gesto de voltar do WKWebView mostra uma FOTO da tela anterior enquanto o
// dedo arrasta. Lido na fonte do WebKit (`ViewGestureControllerIOS.mm`): a foto
// só é usada se o item do histórico deixa o WebKit restaurar a rolagem OU se a
// rolagem ATUAL do documento é idêntica à do momento da foto. Senão, só a cor
// de fundo. O `ScrollToTop` desliga a restauração nativa de propósito
// (`scrollRestoration = "manual"`, BUG-170), e todo PUSH vai ao topo — então,
// com o documento rolando, as duas rolagens quase nunca batiam e o gesto
// mostrava cor sólida "sempre" (relato do fundador, 1.11).
//
// Com o documento PARADO em 0 e a rolagem dentro do `#app-scroll-root`, a
// comparação do WebKit é 0 contra 0 e a foto aparece — confirmado em aparelho
// no caso particular (Home sem rolar → livro → voltar mostrou a tela certa).
//
// Por que só iOS nativo: é o único lugar com o gesto e a foto. Na web o
// scroller interno mataria a auto-ocultação da barra de endereço do Safari e do
// Chrome (ver `appScroll.ts`), e o Android não tem esse gesto.
//
// O CSS que faz o scroller é `html[data-app-scroll="frame"]` em `theme.css`,
// limitado à largura de celular (no iPad o frame de desktop já rola por dentro).
// ---------------------------------------------------------------------------

import { nativePlatform, type NativePlatform } from "../platform/native";
import { getAppScroller, scrollAppTo } from "./appScroll";

/** Atributo no `<html>` que liga o scroller do frame no celular. */
export const FRAME_SCROLL_ATTR = "data-app-scroll";
export const FRAME_SCROLL_VALUE = "frame";

/**
 * Marca o `<html>` antes do primeiro render, para a primeira pintura já nascer
 * com o scroller certo (marcar depois faria o layout pular no boot).
 */
export function applyFrameScrollMode(
  root: HTMLElement = document.documentElement,
  platform: NativePlatform | null = nativePlatform(),
): boolean {
  if (platform !== "ios") return false;
  root.setAttribute(FRAME_SCROLL_ATTR, FRAME_SCROLL_VALUE);
  return true;
}

/**
 * Toque na status bar leva ao topo.
 *
 * O iOS só faz isso sozinho com o scroll view do WKWebView — que é o
 * DOCUMENTO, e ele agora não rola. O `AppDelegate` repassa a notificação
 * `capacitorStatusBarTapped` como o evento `statusTap` na `window`; aqui ele
 * vira rolagem do scroller ativo. Sem scroller (rota fora do frame), o
 * documento rola e o iOS já resolve: não fazer nada é o certo.
 */
export function installStatusTapToTop(target: Window = window): () => void {
  const onTap = () => {
    if (!getAppScroller()) return;
    scrollAppTo(0, "smooth");
  };
  target.addEventListener("statusTap", onTap);
  return () => target.removeEventListener("statusTap", onTap);
}
